import {
  onlineManager,
  useIsFetching,
  useIsMutating,
  useMutationState,
} from "@tanstack/react-query";

import { useAuth } from "~/hooks/useAuth";

export type SyncStatus = {
  state: "signed-out" | "loading" | "syncing" | "offline" | "synced";
  label: string;
  hint: string;
};

/**
 * A guest's tasks are local-only, so "not signed in" is a normal resting
 * state, not an error. For a signed-in user this reflects pending writes and
 * connectivity — a failed request means "not uploaded yet", never "lost".
 */
export function useSyncStatus(): SyncStatus {
  const { user } = useAuth();
  const fetching = useIsFetching();
  const mutating = useIsMutating();
  const paused = useMutationState({
    filters: { predicate: (m) => m.state.isPaused },
  }).length;

  if (!user) {
    return {
      state: "signed-out",
      label: "On this device",
      hint: "Sign up to sync and back up",
    };
  }
  if (paused > 0 || !onlineManager.isOnline()) {
    return {
      state: "offline",
      label: "Saved on this device",
      hint: "Will sync when you're back online",
    };
  }
  if (mutating > 0) {
    return {
      state: "syncing",
      label: "Syncing",
      hint: "Sending your latest changes",
    };
  }
  if (fetching > 0) {
    return { state: "loading", label: "Loading", hint: "Opening your tasks" };
  }
  return {
    state: "synced",
    label: "All synced",
    hint: "Everything is backed up",
  };
}
